import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NavigationExtras, Params } from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

export type SlideOverWidth =
  | 'xsmall'
  | 'small'
  | 'small-split'
  | 'medium'
  | 'large';

export interface SlideOverState {
  width?: SlideOverWidth;
  queryParamKeys: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SlideOverService {
  readonly #router = inject(Router);

  readonly #slideOverIsOpen = signal(false);
  readonly #slideOverWidth = signal<SlideOverWidth>('large');

  readonly isOpen = this.#slideOverIsOpen.asReadonly();
  readonly slideOverWidth = this.#slideOverWidth.asReadonly();

  public openSlideOver() {
    this.#slideOverIsOpen.set(true);
  }

  public closeSlideOver(queryParams?: Params) {
    let extras: NavigationExtras = {
      queryParams: { backToSlideOver: undefined },
      queryParamsHandling: 'merge',
    };
    if (queryParams) {
      extras = {
        queryParams: { ...queryParams, backToSlideOver: undefined },
        queryParamsHandling: 'merge',
      };
    }
    void this.#router.navigate([{ outlets: { slideOver: null } }], extras);
    this.#slideOverIsOpen.set(false);
  }

  protected readonly isSlideOverClosed = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => {
        this.closeSlideoverOnUrlChange(
          !event.urlAfterRedirects.includes('(slideOver:'),
        );
      }),
    ),
  );

  closeSlideoverOnUrlChange(isSlideOverClosed: boolean) {
    if (isSlideOverClosed && this.#slideOverIsOpen()) {
      this.closeSlideOver();
    }
  }

  public setWidth(width: SlideOverWidth) {
    this.#slideOverWidth.set(width);
  }

  public resetSlideOverState() {
    this.#slideOverWidth.set('large');
  }
}
